import Vue from 'vue';
import VueRouter from 'vue-router';
import RouterPrefetch from 'vue-router-prefetch'
import DashboardPlugin from './plugins/dashboard-plugin';
import App from './App.vue';
import { BootstrapVueIcons } from 'bootstrap-vue'

// iOS Safari zooms the page when a form field takes focus, including the
// read-only input behind every select. Capping the viewport scale at 1 stops
// that focus zoom; Safari has ignored the cap for pinch zoom since iOS 10, so
// accessibility zoom still works. iOS only: Android honours the cap and would
// lose pinch zoom.
(function preventIosFocusZoom() {
  const ua = navigator.userAgent || ''
  const isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (!isIos) return
  const meta = document.querySelector('meta[name="viewport"]')
  if (meta) meta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1')
})()

// Enhanced script loading error handler - add this early in main.js
window.addEventListener('error', (event) => {
  // Handle script tag loading failures (your main issue)
  if (event.target && event.target.tagName === 'SCRIPT' && event.target.src) {
    console.error('[APP] Script loading failed:', event.target.src);
    console.log('[APP] This is likely due to stale cache after deployment');

    // Force reload with cache busting
    const url = new URL(window.location);
    url.searchParams.set('_cb', Date.now());
    url.searchParams.set('_src', 'script_error');
    window.location.replace(url.toString());
    return;
  }

  // Handle chunk loading errors
  if (event.message &&
    (event.message.includes('Loading chunk') ||
      event.message.includes('ChunkLoadError'))) {
    console.warn('[APP] Chunk load error detected:', event.message);
    handleStaleCache('chunk_error');
  }
});

// Enhanced unhandled rejection handler
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message &&
    (event.reason.message.includes('Loading chunk') ||
      event.reason.message.includes('ChunkLoadError') ||
      event.reason.message.includes('Loading CSS chunk'))) {
    console.warn('[APP] Unhandled rejection due to chunk loading:', event.reason.message);
    handleStaleCache('chunk_rejection');
  }
});

// Centralized stale cache handler
async function handleStaleCache(source) {
  console.log(`[APP] Handling stale cache from: ${source}`);

  try {
    // Try to update service worker first
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      console.log('[APP] Attempting to update service worker...');
      await reg.update();

      if (reg.waiting) {
        console.log('[APP] Found waiting service worker, activating...');
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });

        // Wait for activation then reload
        reg.waiting.addEventListener('statechange', (e) => {
          if (e.target.state === 'activated') {
            console.log('[APP] New SW activated. Reloading...');
            forceReload(source);
          }
        });
        return;
      }
    }
  } catch (e) {
    console.error('[APP] Error updating service worker:', e);
  }

  // Fallback: force reload immediately
  forceReload(source);
}

function forceReload(source) {
  const url = new URL(window.location);
  url.searchParams.set('_cb', Date.now());
  url.searchParams.set('_src', source);
  console.log(`[APP] Force reloading due to ${source}:`, url.toString());
  window.location.replace(url.toString());
}

// Force service worker update on page load
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        console.log('[APP] Forcing service worker update check...');
        await reg.update();
      }
    } catch (e) {
      console.error('[APP] Error checking for SW updates:', e);
    }
  });
}


// Amplify setup
import Amplify, * as AmplifyModules from 'aws-amplify'
import { AmplifyPlugin } from 'aws-amplify-vue'
import awsconfig from './aws-exports'
Amplify.configure(awsconfig)
Vue.use(AmplifyPlugin, AmplifyModules)

//Mask filter
import VueMask from 'v-mask'
Vue.use(VueMask);

Vue.use(BootstrapVueIcons)

// Axios
import axios from 'axios'
import VueAxios from 'vue-axios'
import { installGroupDataEviction } from '@/util/groupDataCache'
import { installGuard } from '@/util/groupRequestGuard'
Vue.use(VueAxios, axios)
// Evicts cached group reference data whenever a write hits one of its resources
installGroupDataEviction(axios)
// Refuses writes from a tab left on a location another tab switched away from
installGuard(axios)

//Signature
import VueSignaturePad from 'vue-signature-pad';
Vue.use(VueSignaturePad);

// router setup
import router from './routes/router';
import i18n from './i18n';
import './registerServiceWorker'
// plugin setup
Vue.use(DashboardPlugin);
Vue.use(VueRouter);
Vue.use(RouterPrefetch);

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch(err => err);
};

// Global fix: close Element UI dropdown menus after item click.
// Needed because the app uses <a class="dropdown-item"> inside <el-dropdown-menu>
// instead of <el-dropdown-item>, so Element UI's command event never fires.
// Capture phase ensures this runs before any stopPropagation in child handlers.
document.addEventListener('click', (e) => {
  const item = e.target.closest('.dropdown-item') || e.target.closest('.el-dropdown-menu__item');
  if (!item) return;
  const menuEl = e.target.closest('.el-dropdown-menu');
  if (!menuEl) return;

  Vue.nextTick(() => {
    let vm = menuEl.__vue__;
    if (!vm) return;
    let depth = 0;
    while (vm && depth < 50) {
      if (vm.$options && vm.$options.name === 'ElDropdown' && typeof vm.hide === 'function') {
        vm.hide();
        return;
      }
      // Element UI 2.x internal: ElDropdownMenu holds a ref to its parent ElDropdown
      if (vm.dropdown && typeof vm.dropdown.hide === 'function') {
        vm.dropdown.hide();
        return;
      }
      // Fallback: directly close the menu popper, scoped to ElDropdownMenu only
      // to avoid accidentally closing tooltips/popovers/selects that also use showPopper
      if (vm.$options && vm.$options.name === 'ElDropdownMenu' && vm.showPopper !== undefined) {
        vm.showPopper = false;
        return;
      }
      vm = vm.$parent;
      depth++;
    }
  });
}, true);

/* eslint-disable no-new */
new Vue({
  el: '#app',
  render: h => h(App),
  router,
  i18n
});
